import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import Loader from 'react-loader-spinner';
import { FiSave, FiStar, FiUserPlus, FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import fieldImage from '../images/sqField2.png';
import defaultPlayerImage from '../images/defaultAvatar.png';
import '../styles/leagueTourTeamManagement.css';

const POSSIBLE_FORMATIONS = [
  [3, 3, 1], [3, 2, 2], [3, 1, 2], [2, 1, 2],
  [2, 2, 0], [2, 1, 1], [4, 4, 2], [4, 3, 3],
  [2, 4, 1], [2, 3, 2], [2, 2, 3], [1, 0, 2],
  [1, 3, 3], [3, 1, 3], [3, 3, 2],
];

const isSameFormation = (a, b) => {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => Number(v) === Number(b[i]));
};

const formatFormationLabel = (formation) => `${formation[0]}-${formation[1]}-${formation[2]}`;

function LeagueTourTeamManagement() {
  const dispatch = useDispatch();

  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [selectedScheme, setSelectedScheme] = useState(0);

  const [leaguePlayers, setLeaguePlayers] = useState([]);
  const [tourTeams, setTourTeams] = useState([]);
  const [lineupPlayers, setLineupPlayers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeSlotIndex, setActiveSlotIndex] = useState(null);
  const [playerSearch, setPlayerSearch] = useState('');
  const [playersSearched, setPlayersSearched] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const selectedFormation = POSSIBLE_FORMATIONS[selectedScheme] || POSSIBLE_FORMATIONS[0];

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }

    const tokens = getTokensFromStore();
    return {
      Authorization: 'Bearer ' + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const extractList = (payload, keys = []) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
      if (payload[key] && Array.isArray(payload[key].data)) {
        return payload[key].data;
      }
    }

    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    return [];
  };

  const findByValue = (items, value) => items.find((item) => String(item.value) === String(value));

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map((item) => item.item);
    };
  };

  const buildSlots = (formation) => {
    const [defenders, midfielders, forwards] = formation;
    const allRoles = [
      { role: 'gk', count: 1 },
      { role: 'df', count: defenders },
      { role: 'mf', count: midfielders },
      { role: 'fw', count: forwards },
    ];

    // Only include rows that have players
    const activeRoles = allRoles.filter((row) => row.count > 0);

    const TOP_Y = 28;
    const BOTTOM_Y = 84;

    // Distribute y positions evenly across active rows
    const yPositions = activeRoles.map((_, i) => {
      if (activeRoles.length === 1) return BOTTOM_Y;
      return BOTTOM_Y - (i * (BOTTOM_Y - TOP_Y)) / (activeRoles.length - 1);
    });

    const slots = [];
    let idx = 0;

    activeRoles.forEach((row, rowIndex) => {
      const y = yPositions[rowIndex];
      for (let i = 0; i < row.count; i += 1) {
        const x = row.count === 1 ? 50 : ((i + 1) * 100) / (row.count + 1);
        slots.push({
          index: idx,
          role: row.role,
          x,
          y,
        });
        idx += 1;
      }
    });

    return slots;
  };

  const slots = useMemo(() => buildSlots(selectedFormation), [selectedFormation]);

  const activeSlotPlayer = useMemo(() => {
    if (activeSlotIndex === null) {
      return null;
    }

    return lineupPlayers.find((item) => item.index === activeSlotIndex) || null;
  }, [activeSlotIndex, lineupPlayers]);

  const getIndexFromPositionId = (positionId, formation) => {
    if (!positionId || typeof positionId !== 'string') return null;

    // Parse "4-1" or "4_1" format
    const parts = positionId.replace(/_/g, '-').split('-').map(Number);
    if (parts.length !== 2) return null;

    const [roleNumber, positionInRole] = parts;

    // Build slots for this formation
    const testSlots = buildSlots(formation);

    const hasFw = testSlots.some((s) => s.role === 'fw');
    const hasMf = testSlots.some((s) => s.role === 'mf');

    // Map role number to the actual role:
    // Server sends 1 for topmost row — FW if exists, else MF, else DF
    let targetRole;
    if (roleNumber === 4) {
      targetRole = 'gk';
    } else if (roleNumber === 3) {
      targetRole = 'df';
    } else if (roleNumber === 2) {
      targetRole = 'mf';
    } else if (roleNumber === 1) {
      if (hasFw) targetRole = 'fw';
      else if (hasMf) targetRole = 'mf';
      else targetRole = 'df';
    }

    if (!targetRole) return null;

    const slotsWithRole = testSlots.filter((s) => s.role === targetRole);
    if (positionInRole <= 0 || positionInRole > slotsWithRole.length) return null;

    return slotsWithRole[positionInRole - 1]?.index ?? null;
  };

  const applyLineupFromTour = (tourTeam, schemeIndex) => {
    const formation = POSSIBLE_FORMATIONS[schemeIndex] || POSSIBLE_FORMATIONS[0];
    const totalSlots = 1 + formation[0] + formation[1] + formation[2];
    const players = Array.isArray(tourTeam?.players) ? tourTeam.players : [];

    const prepared = players
      .map((item, idx) => {
        let index = item.index;
        
        // Try to convert from position or position_id field
        if (typeof index !== 'number') {
          const positionId = item.position || item.position_id;
          if (positionId) {
            const convertedIndex = getIndexFromPositionId(positionId, formation);
            index = typeof convertedIndex === 'number' ? convertedIndex : idx;
          } else {
            index = idx;
          }
        }

        return {
          ...item,
          index,
          star: !!item.star,
        };
      })
      .filter((item) => item.index < totalSlots);

    const firstStarIndex = prepared.findIndex((item) => !!item.star);
    const normalizedPrepared = prepared.map((item, index) => ({
      ...item,
      star: firstStarIndex === -1 ? false : index === firstStarIndex,
    }));

    setLineupPlayers(normalizedPrepared);
  };

  const initEmptyLineup = (schemeIndex) => {
    const formation = POSSIBLE_FORMATIONS[schemeIndex] || POSSIBLE_FORMATIONS[0];
    const totalSlots = 1 + formation[0] + formation[1] + formation[2];
    const prepared = [];
    for (let i = 0; i < totalSlots; i += 1) {
      prepared.push({ index: i, star: false });
    }
    setLineupPlayers(prepared);
  };

  const getSchemeIndexFromTourTeam = (tourTeam) => {
    if (!tourTeam) {
      return 0;
    }

    if (Array.isArray(tourTeam.scheme_id)) {
      const index = POSSIBLE_FORMATIONS.findIndex((item) => isSameFormation(item, tourTeam.scheme_id));
      return index >= 0 ? index : 0;
    }

    if (Number.isInteger(tourTeam.scheme_id)) {
      return tourTeam.scheme_id >= 0 && tourTeam.scheme_id < POSSIBLE_FORMATIONS.length ? tourTeam.scheme_id : 0;
    }

    return 0;
  };

  const syncTourLineup = (tourValue, preferredScheme) => {
    const selected = findByValue(tours, tourValue);
    setSelectedTour(selected || null);

    const tourTeam = selected ? tourTeams.find((item) => Number(item.stage_id) === Number(selected.value)) : null;
    const schemeIndex = typeof preferredScheme === 'number'
      ? preferredScheme
      : getSchemeIndexFromTourTeam(tourTeam);

    setSelectedScheme(schemeIndex);

    if (tourTeam) {
      applyLineupFromTour(tourTeam, schemeIndex);
    } else {
      initEmptyLineup(schemeIndex);
    }
  };

  const loadChampionships = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/leagues/1/adminLeagues',
        {},
        { headers }
      );

      const leaguesData = extractList(response.data);
      const championshipsMap = {};

      leaguesData.forEach((item) => {
        const championshipName = item.championat_name || 'Без чемпионата';

        if (!championshipsMap[championshipName]) {
          championshipsMap[championshipName] = {
            value: championshipName,
            name: championshipName,
            leagues: []
          };
        }

        if (item.league_id > 0) {
          championshipsMap[championshipName].leagues.push({
            value: item.league_id,
            name: item.league_name,
            seasons: item.seasons || [],
            ...item,
          });
        }
      });

      setChampionships(
        Object.values(championshipsMap)
          .filter((item) => item.leagues.length > 0)
          .map((item, index) => ({ ...item, key: index }))
      );
    } catch (error) {
      console.error('Error loading championships:', error);
      setChampionships([]);
      setMessage({ text: 'Не удалось загрузить список лиг', type: 'error' });
    }
  };

  const loadTours = async (leagueId, seasonId) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      BASE_URL.url + `/leagues/${leagueId}/getLeagueStages`,
      {
        league_id: leagueId,
        season_id: seasonId,
      },
      { headers }
    );

    const stagesData = extractList(response.data);
    const preparedTours = stagesData.map((item, index) => ({
      ...item,
      value: item.id,
      name: item.name,
      key: index,
    }));

    setTours(preparedTours);
    return preparedTours;
  };

  const loadLeagueTourTeams = async (leagueId, seasonId) => {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      BASE_URL.url + `/leagues/${leagueId}/getLeagueTourTeams`,
      {
        league_id: leagueId,
        season_id: seasonId,
      },
      { headers }
    );

    const preparedTourTeams = extractList(response.data, ['tour_teams', 'teams', 'items']);
    setTourTeams(preparedTourTeams);
    return preparedTourTeams;
  };

  const loadLeaguePlayers = async (leagueId, seasonId) => {
    setLoadingPlayers(true);
    try {
      const headers = await getAuthHeaders();
      const searchValue = playerSearch.trim();
      const searchParts = searchValue.split(' ').filter((part) => part.length > 0);
      const response = await axios.post(
        BASE_URL.url + '/players/get_league_players',
        {
          league_id: leagueId,
          season_id: seasonId,
          search: searchValue,
          family: searchParts[0] || undefined,
          name: searchParts[1] || undefined,
        },
        { headers }
      );

      const players = extractList(response.data, ['players']).map((item) => ({
        ...item,
        player_id: item.player_id || item.id,
        key: item.player_id || item.id,
        player_image: item.player_image || item.image || item.photo || item.player_photo || null,
        team_image: item.team_image || item.team_logo || null,
      }));

      setLeaguePlayers(players);
      setPlayersSearched(true);
      return players;
    } catch (error) {
      console.error('Error loading league players:', error);
      setLeaguePlayers([]);
      setPlayersSearched(true);
      throw error;
    } finally {
      setLoadingPlayers(false);
    }
  };

  const loadPageData = async (leagueId, seasonId) => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const [loadedTours, loadedTourTeams] = await Promise.all([
        loadTours(leagueId, seasonId),
        loadLeagueTourTeams(leagueId, seasonId),
      ]);

      if (loadedTours.length === 0) {
        setSelectedTour(null);
        initEmptyLineup(0);
        return;
      }

      const firstTour = loadedTours[0];
      const firstTourTeam = loadedTourTeams.find((item) => Number(item.stage_id) === Number(firstTour.value));
      const schemeIndex = getSchemeIndexFromTourTeam(firstTourTeam);

      setSelectedTour(firstTour);
      setSelectedScheme(schemeIndex);

      if (firstTourTeam) {
        applyLineupFromTour(firstTourTeam, schemeIndex);
      } else {
        initEmptyLineup(schemeIndex);
      }
    } catch (error) {
      setMessage({ text: 'Не удалось загрузить составы тура', type: 'error' });
      setTours([]);
      setTourTeams([]);
      setLeaguePlayers([]);
      setPlayersSearched(false);
      setSelectedTour(null);
      initEmptyLineup(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChampionships();
  }, []);

  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      loadPageData(selectedLeague.value, selectedSeason.value);
    } else {
      setTours([]);
      setTourTeams([]);
      setLeaguePlayers([]);
      setPlayersSearched(false);
      setSelectedTour(null);
      initEmptyLineup(0);
    }
  }, [selectedLeague, selectedSeason]);

  const handleChampionshipChange = (championshipId) => {
    const championship = findByValue(championships, championshipId);
    setSelectedChampionship(championship || null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setMessage({ text: '', type: '' });

    if (championship && championship.leagues && championship.leagues.length > 0) {
      setLeagues(championship.leagues);
    } else {
      setLeagues([]);
    }

    setSeasons([]);
  };

  const handleLeagueChange = (leagueId) => {
    const league = findByValue(leagues, leagueId);
    setSelectedLeague(league || null);
    setSelectedSeason(null);
    setMessage({ text: '', type: '' });

    if (league && league.seasons && league.seasons.length > 0) {
      setSeasons(
        league.seasons.map((item, index) => ({
          value: item.season_id,
          name: item.season_name,
          key: index,
          ...item,
        }))
      );
    } else {
      setSeasons([]);
    }
  };

  const handleSeasonChange = (seasonId) => {
    const season = findByValue(seasons, seasonId);
    setSelectedSeason(season || null);
    setMessage({ text: '', type: '' });
  };

  const handleTourChange = (tourId) => {
    syncTourLineup(tourId);
    setMessage({ text: '', type: '' });
  };

  const handleSchemeChange = (schemeId) => {
    const schemeIndex = Number(schemeId);
    const playersByIndex = {};
    lineupPlayers.forEach((item) => {
      if (item && typeof item.index === 'number') {
        playersByIndex[item.index] = item;
      }
    });

    const formation = POSSIBLE_FORMATIONS[schemeIndex] || POSSIBLE_FORMATIONS[0];
    const totalSlots = 1 + formation[0] + formation[1] + formation[2];

    const nextLineup = [];
    for (let i = 0; i < totalSlots; i += 1) {
      nextLineup.push(playersByIndex[i] ? { ...playersByIndex[i], index: i } : { index: i, star: false });
    }

    setSelectedScheme(schemeIndex);
    setLineupPlayers(nextLineup);
    setMessage({ text: '', type: '' });
  };

  const openPlayerPicker = (slotIndex) => {
    setActiveSlotIndex(slotIndex);
    setPlayerSearch('');
    setPlayersSearched(false);
    setLeaguePlayers([]);
    setPickerOpen(true);
  };

  const closePlayerPicker = () => {
    setPickerOpen(false);
    setActiveSlotIndex(null);
    setPlayerSearch('');
    setPlayersSearched(false);
  };

  const handleSearchPlayers = async () => {
    if (!selectedLeague || !selectedSeason) {
      return;
    }

    await loadLeaguePlayers(selectedLeague.value, selectedSeason.value);
  };

  const setPlayerToActiveSlot = (player) => {
    if (activeSlotIndex === null) {
      return;
    }

    setLineupPlayers((prev) => {
      // Игрок переезжает на новую позицию, а не дублируется в двух
      const next = prev.filter((item) => item.index !== activeSlotIndex
        && String(item.player_id) !== String(player.player_id));
      next.push({
        index: activeSlotIndex,
        player_id: player.player_id,
        name: player.name,
        family: player.family,
        player_image: player.player_image,
        team_id: player.team_id,
        team_name: player.team_name,
        team_image: player.team_image,
        star: false,
      });

      return next.sort((a, b) => a.index - b.index);
    });

    setMessage({ text: '', type: '' });
    closePlayerPicker();
  };

  const removePlayerFromSlot = (slotIndex) => {
    setLineupPlayers((prev) => {
      const next = prev.filter((item) => item.index !== slotIndex);
      next.push({ index: slotIndex, star: false });
      return next.sort((a, b) => a.index - b.index);
    });
  };

  const toggleStar = (slotIndex) => {
    setLineupPlayers((prev) => {
      const currentPlayer = prev.find((item) => item.index === slotIndex);
      if (!currentPlayer || !currentPlayer.player_id) {
        return prev;
      }

      const shouldEnableStar = !currentPlayer.star;

      return prev.map((item) => {
        if (!item.player_id) {
          return {
            ...item,
            star: false,
          };
        }

        if (item.index === slotIndex) {
          return {
            ...item,
            star: shouldEnableStar,
          };
        }

        return {
          ...item,
          star: false,
        };
      });
    });
  };

  const getPlayerBySlotIndex = (slotIndex) => {
    console.log('Getting player for slot index:', slotIndex, lineupPlayers);
    return lineupPlayers.find((item) => item.index === slotIndex) || { index: slotIndex, star: false };
  };

  const getPlayerImage = (item) => {
    if (!item || !item.player_image) {
      return defaultPlayerImage;
    }

    if (String(item.player_image).startsWith('/')) {
      return BASE_URL.url + item.player_image;
    }

    return item.player_image;
  };

  const getPositionId = (index) => {
    const slot = slots.find((s) => s.index === index);
    if (!slot) return null;

    const hasFw = slots.some((s) => s.role === 'fw');

    // Server expects: GK=4, DF=3, MF=2 (or 1 if no FW), FW=1
    let roleNumber;
    if (slot.role === 'gk') {
      roleNumber = 4;
    } else if (slot.role === 'df') {
      roleNumber = 3;
    } else if (slot.role === 'mf') {
      roleNumber = hasFw ? 2 : 1;
    } else if (slot.role === 'fw') {
      roleNumber = 1;
    }

    const slotsWithSameRole = slots.filter((s) => s.role === slot.role);
    const positionInRole = slotsWithSameRole.findIndex((s) => s.index === index) + 1;

    return `${roleNumber}_${positionInRole}`;
  };

  const getPlayersForSave = (players) => {
    if (!Array.isArray(players)) {
      return [];
    }
    return players
      .filter((item) => item && item.player_id)
      .map((item) => ({
        player_id: item.player_id,
        team_id: item.team_id,
        index: getPositionId(item.index),
        star: item.star ? 1 : 0,
      }));
  };

  const handleSave = async () => {
    if (!selectedLeague || !selectedSeason || !selectedTour) {
      setMessage({ text: 'Выберите лигу, сезон и тур', type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const headers = await getAuthHeaders();
      const players = getPlayersForSave(lineupPlayers);

      await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/addTourTeam`,
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
          stage_id: selectedTour.value,
          players,
          scheme: selectedScheme,
        },
        { headers }
      );

      setMessage({ text: 'Сборная тура успешно сохранена', type: 'success' });
      await loadLeagueTourTeams(selectedLeague.value, selectedSeason.value);
    } catch (error) {
      console.error('Error saving league tour team:', error);
      setMessage({ text: 'Не удалось сохранить сборную тура', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const schemeOptions = POSSIBLE_FORMATIONS.map((formation, index) => ({
    value: index,
    name: formatFormationLabel(formation),
    key: index,
  }));

  return (
    <div className="layoutWrapper league-tour-team-page">
      <div className="matchCenterWrapper">
        <div className="matchCenterHeader">
          <div className="league-tour-team-header-row">
            <div>
              <span className="matchCenterName">Сборная тура лиги</span>
              <p>Выберите лигу, сезон, тур и схему. Нажмите на игрока на поле, чтобы заменить его.</p>
            </div>

            {selectedLeague && selectedSeason && selectedTour && (
              <button
                className="league-tour-team-save-btn"
                onClick={handleSave}
                disabled={saving || loading}
              >
                <FiSave />
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            )}
          </div>

          <div className="league-tour-team-filters">
            <div className="league-tour-team-filter-group">
              <label>Чемпионат</label>
              <SelectSearch
                options={championships}
                search
                value={selectedChampionship?.value}
                onChange={handleChampionshipChange}
                filterOptions={fuzzySearch}
                emptyMessage="Чемпионат не найден"
                placeholder="Выберите чемпионат"
              />
            </div>

            <div className="league-tour-team-filter-group">
              <label>Лига</label>
              <SelectSearch
                options={leagues}
                search
                value={selectedLeague?.value}
                onChange={handleLeagueChange}
                filterOptions={fuzzySearch}
                emptyMessage="Лига не найдена"
                placeholder="Выберите лигу"
                disabled={!selectedChampionship}
              />
            </div>

            <div className="league-tour-team-filter-group">
              <label>Сезон</label>
              <SelectSearch
                options={seasons}
                search
                value={selectedSeason?.value}
                onChange={handleSeasonChange}
                filterOptions={fuzzySearch}
                emptyMessage="Сезон не найден"
                placeholder="Выберите сезон"
                disabled={!selectedLeague}
              />
            </div>

            <div className="league-tour-team-filter-group">
              <label>Тур</label>
              <SelectSearch
                options={tours}
                search
                value={selectedTour?.value}
                onChange={handleTourChange}
                filterOptions={fuzzySearch}
                emptyMessage="Тур не найден"
                placeholder="Выберите тур"
                disabled={!selectedSeason}
              />
            </div>

            <div className="league-tour-team-filter-group">
              <label>Схема</label>
              <SelectSearch
                options={schemeOptions}
                search
                value={selectedScheme}
                onChange={handleSchemeChange}
                emptyMessage="Схема не найдена"
                placeholder="Выберите схему"
                disabled={!selectedTour}
              />
            </div>
          </div>
        </div>

        <div className="league-tour-team-container">
          {message.text && (
            <div className={`league-tour-team-message ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="league-tour-team-loading">
              <Loader type="Rings" color="#04a9f5" height={50} width={50} />
              <span>Загрузка данных сборной тура...</span>
            </div>
          ) : selectedLeague && selectedSeason && selectedTour ? (
            <div className="league-tour-team-content">
              <div className="league-tour-team-field-wrapper">
                <div className="league-tour-team-field">
                  <img src={fieldImage} alt="Football field" className="league-tour-team-field-image" />

                  {slots.map((slot) => {
                    const player = getPlayerBySlotIndex(slot.index);
                    const hasPlayer = !!player.player_id;

                    return (
                      <button
                        key={slot.index}
                        type="button"
                        className={`league-tour-team-slot ${hasPlayer ? 'filled' : 'empty'}`}
                        style={{ left: `${slot.x}%`, top: `${slot.y}%` }}
                        onClick={() => openPlayerPicker(slot.index)}
                      >
                        {hasPlayer ? (
                          <img
                            src={getPlayerImage(player)}
                            alt={`${player.family || ''} ${player.name || ''}`.trim()}
                            className="league-tour-team-slot-image"
                            onError={(e) => { e.target.src = defaultPlayerImage; }}
                          />
                        ) : (
                          <FiUserPlus />
                        )}
                        {hasPlayer && player.star && (
                          <span className="league-tour-team-slot-star">
                            <FiStar />
                          </span>
                        )}
                        <span className="league-tour-team-slot-label">
                          {hasPlayer ? `${player.family || ''} ${player.name || ''}`.trim() : 'Выбрать'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="league-tour-team-players-list">
                <h3>Игроки в сборной</h3>
                {slots.map((slot) => {
                  const player = getPlayerBySlotIndex(slot.index);
                  const playerName = player.player_id ? `${player.family || ''} ${player.name || ''}`.trim() : 'Не выбран';

                  return (
                    <div key={`player-row-${slot.index}`} className="league-tour-team-player-row">
                      <div className="league-tour-team-player-meta">
                        <span className="league-tour-team-player-index">#{slot.index + 1}</span>
                        <span className="league-tour-team-player-name">{playerName}</span>
                      </div>

                      <div className="league-tour-team-player-actions">
                        <button
                          type="button"
                          className={`league-tour-team-star-btn ${player.star ? 'active' : ''}`}
                          onClick={() => toggleStar(slot.index)}
                          disabled={!player.player_id}
                        >
                          <FiStar />
                        </button>
                        <button
                          type="button"
                          className="league-tour-team-change-btn"
                          onClick={() => openPlayerPicker(slot.index)}
                        >
                          Изменить
                        </button>
                        <button
                          type="button"
                          className="league-tour-team-remove-btn"
                          onClick={() => removePlayerFromSlot(slot.index)}
                          disabled={!player.player_id}
                        >
                          <FiX />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="league-tour-team-empty-state">
              <h3>Выберите лигу, сезон и тур</h3>
              <p>После выбора будет загружена сборная тура и список игроков лиги.</p>
            </div>
          )}
        </div>
      </div>

      {pickerOpen && (
        <div className="league-tour-team-picker-overlay" onClick={closePlayerPicker}>
          <div className="league-tour-team-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="league-tour-team-picker-header">
              <h3>Выбор игрока для позиции #{activeSlotIndex !== null ? activeSlotIndex + 1 : '-'}</h3>
              <button type="button" className="league-tour-team-picker-close" onClick={closePlayerPicker}>
                <FiX />
              </button>
            </div>

            <div className="league-tour-team-picker-body">
              <div className="league-tour-team-picker-search-row">
                <input
                  type="text"
                  className="league-tour-team-picker-search"
                  placeholder="Поиск по фамилии"
                  value={playerSearch}
                  onChange={(e) => setPlayerSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchPlayers();
                    }
                  }}
                />
                <button
                  type="button"
                  className="league-tour-team-picker-search-btn"
                  onClick={handleSearchPlayers}
                  disabled={loadingPlayers}
                >
                  {loadingPlayers ? 'Поиск...' : 'Искать'}
                </button>
              </div>

              {loadingPlayers ? (
                <div className="league-tour-team-picker-loading">Загрузка игроков...</div>
              ) : (
                <div className="league-tour-team-picker-list">
                  {leaguePlayers.length > 0 ? (
                    leaguePlayers.map((player) => (
                      <button
                        type="button"
                        key={player.player_id}
                        className={`league-tour-team-picker-item ${activeSlotPlayer?.player_id === player.player_id ? 'selected' : ''}`}
                        onClick={() => setPlayerToActiveSlot(player)}
                      >
                        <img
                          src={getPlayerImage(player)}
                          alt={`${player.family || ''} ${player.name || ''}`.trim()}
                          className="league-tour-team-picker-item-image"
                          onError={(e) => { e.target.src = defaultPlayerImage; }}
                        />
                        <span className="league-tour-team-picker-item-name">{`${player.family || ''} ${player.name || ''}`.trim()}</span>
                        <span className="league-tour-team-picker-item-team">{player.team_name || 'Без команды'}</span>
                      </button>
                    ))
                  ) : playersSearched ? (
                    <div className="league-tour-team-picker-empty">Игроки не найдены</div>
                  ) : (
                    <div className="league-tour-team-picker-empty">Введите запрос и нажмите "Искать"</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeagueTourTeamManagement;